import React, { useEffect, useCallback, useState } from "react";
import { Modal, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useZeroPositionEntryStore } from "../../Store/zeroPositionEntryStore";
import { usePortfolioRebalanceStore } from "../../Store/portfolioRebalanceStore";
import { headerStyle, modalStyle } from "../OrderEntry/styles";
import ZeroPositionEntryForm from "./ZeroPositionEntryForm";
import { tokens } from "../../Common/designTokens";

const containerSx = {
  ...modalStyle,
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh",
  overflow: "hidden",
  width: 720,
};

const contentSx = { p: 2, overflow: "auto", flex: 1 };

const footerSx = {
  flexShrink: 0,
  display: "flex",
  justifyContent: "flex-end",
  gap: 2,
  p: 2,
  borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
  backgroundColor: tokens.colors.defaultGridRowColor,
};

const ZeroPositionEntryModal = ({ open, onClose }) => {
  const {
    setField,
    setBondDetails,
    reset,
    setCalcResults,
    setCusipError,
    setCusipRef, // NEW: allow modal to prefill cusipRef when updating
  } = useZeroPositionEntryStore();

  const { setVirtualZeroPosition, virtualZeroPosition } = usePortfolioRebalanceStore();

  const isUpdateMode = !!virtualZeroPosition;
  const [validationState, setValidationState] = useState({
    canSubmit: false,
    isDuplicateCusip: false,
    formData: null
  });

  // Prefill data when in update mode and modal opens
  useEffect(() => {
    if (open && isUpdateMode && virtualZeroPosition) {
      setField("cusip", virtualZeroPosition.cusip || "");
      // Prefill cusipRef if present (fallback to cusip)
      setCusipRef(virtualZeroPosition.cusipRef || virtualZeroPosition.cusip || "");
      setField("price", virtualZeroPosition.price ? String(virtualZeroPosition.price) : "");
      setBondDetails(null);
      setCalcResults(null);
      setCusipError(null);
    }
  }, [open, isUpdateMode, virtualZeroPosition, setField, setBondDetails, setCalcResults, setCusipError]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setCusipError(null);
      setValidationState({ canSubmit: false, isDuplicateCusip: false, formData: null });
    }
  }, [open, reset, setCusipError]);

  // Handle form validation changes
  const handleValidationChange = useCallback((newValidationState) => {
    setValidationState(newValidationState);
  }, []);

  // Submit handler
  const handleSubmit = useCallback(() => {
    const { canSubmit, isDuplicateCusip, formData } = validationState;
    
    if (!canSubmit) return;
    
    if (!isUpdateMode && isDuplicateCusip) {
      setCusipError(
        "This Security already exists in your holdings. You cannot add a zero position entry for an existing holding."
      );
      return;
    }

    if (formData) {
      setVirtualZeroPosition({
        cusip: formData.cusip,
        price: Number(formData.price),
        qty: 0,
        bondCalcResults: formData.storeCalcResults,
        bondDetails: formData.bondDetails,
      });
    }
    
    onClose?.();
  }, [validationState, isUpdateMode, setVirtualZeroPosition, onClose, setCusipError]);

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose?.();
      }}
      disableEscapeKeyDown={true}
      aria-labelledby="zero-position-entry-title"
    >
      <Box sx={containerSx}>
        {/* Header */}
        <Box sx={{ ...headerStyle, flexShrink: 0, position: "relative" }}>
          <Typography id="zero-position-entry-title" variant="h6" component="h2" sx={{ fontWeight: "bold" }}>
            {isUpdateMode ? "Update Security" : "Add Security"}
          </Typography>

          {/* NEW: close icon top-right that behaves like Cancel */}
          <IconButton
            aria-label="close"
            onClick={() => onClose?.()}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: tokens.colors.fontColour85,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={contentSx}>
          <ZeroPositionEntryForm onValidationChange={handleValidationChange} />
        </Box>

        {/* Footer */}
        <Box sx={footerSx}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontWeight: "700",
              px: 4,
              py: 1,
              width: "150px",
              height: "46px",
              borderRadius: "8px",
              
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!validationState.canSubmit}
            sx={{
              textTransform: "none",
              fontWeight: "700",
              px: 4,
              py: 1,
              width: "150px",
              height: "46px",
              borderRadius: "8px",
              backgroundColor: tokens.palette.primary,
              color: tokens.colors.white,
              "&:hover": { backgroundColor: tokens.palette.primaryA80 },
              opacity: validationState.canSubmit ? 1 : 0.6,
            }}
          >
            {isUpdateMode ? "Update" : "Add"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ZeroPositionEntryModal;
